/**
 * Engagement Intelligence Context
 * 
 * Tracks user engagement from anonymous visitor through authenticated power user.
 * Powers streaks, momentum, personalization, and the EQ Hub.
 * 
 * Features:
 * - Anonymous tracking with localStorage before login
 * - Seamless claim of anonymous data on login
 * - Real-time streak and momentum tracking
 * - Event tracking for all user actions
 * 
 * Hooks:
 * - useEngagement() - Full engagement state and tracking functions
 * - useStreak() - Quick access to streak data
 * - useOnboarding() - Onboarding progress and feature discovery
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/axios';

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEYS = {
  ANONYMOUS_ID: 'eq_engagement_id',
  SESSION_ID: 'eq_session_id',
  PENDING_EVENTS: 'eq_pending_events',
  LOCAL_STREAK: 'eq_local_streak',
  ONBOARDING: 'eq_onboarding_state',
};

const EVENT_BATCH_SIZE = 10;
const EVENT_FLUSH_INTERVAL = 30000; // 30 seconds

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a unique ID
 */
function generateId(prefix = 'eq') {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `${prefix}_${timestamp}_${random}`;
}

/**
 * Get or create anonymous ID
 */
function getAnonymousId() {
  let anonId = localStorage.getItem(STORAGE_KEYS.ANONYMOUS_ID);
  if (!anonId) {
    anonId = generateId('anon');
    localStorage.setItem(STORAGE_KEYS.ANONYMOUS_ID, anonId);
  }
  return anonId;
}

/**
 * Get or create session ID
 */
function getSessionId() {
  let sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!sessionId) {
    sessionId = generateId('sess');
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
}

/**
 * Get pending events from localStorage
 */
function getPendingEvents() {
  try {
    const events = localStorage.getItem(STORAGE_KEYS.PENDING_EVENTS);
    return events ? JSON.parse(events) : [];
  } catch {
    return [];
  }
}

/**
 * Save pending events to localStorage
 */
function savePendingEvents(events) {
  try {
    // Keep only last 100 events to prevent localStorage overflow
    const trimmed = events.slice(-100);
    localStorage.setItem(STORAGE_KEYS.PENDING_EVENTS, JSON.stringify(trimmed));
  } catch (error) {
    console.warn('Failed to save pending events:', error);
  }
}

/**
 * Clear all engagement data from localStorage
 */
function clearLocalEngagement() {
  localStorage.removeItem(STORAGE_KEYS.PENDING_EVENTS);
  // Keep anonymous ID for future sessions if user logs out
  // localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_ID);
}

// ============================================================================
// CONTEXT
// ============================================================================

const EngagementContext = createContext(null);

export function EngagementProvider({ children }) {
  const { isAuthenticated, user: _user, isLoading: authLoading } = useAuth();
  
  // Core state
  const [engagement, setEngagement] = useState(null);
  const [streak, setStreak] = useState({ current: 0, longest: 0, tier: 'starting' });
  const [momentum, setMomentum] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasClaimed, setHasClaimed] = useState(false);
  
  // Onboarding state
  const [onboarding, setOnboarding] = useState({
    step: 0,
    featuresDiscovered: [],
    completed: false,
  });
  
  // IDs
  const anonymousId = useRef(getAnonymousId());
  const sessionId = useRef(getSessionId());
  
  // Event queue
  const eventQueue = useRef([]);
  const flushTimerRef = useRef(null);

  // ============================================================================
  // LOAD ENGAGEMENT DATA
  // ============================================================================
  
  const loadEngagement = useCallback(async () => {
    if (authLoading) return;
    
    try {
      if (isAuthenticated) {
        const response = await api.get('/api/engagement/profile');
        if (response.data?.success) {
          setEngagement(response.data.engagement);
          setStreak({
            current: response.data.engagement.current_streak || 0,
            longest: response.data.engagement.longest_streak || 0,
            tier: response.data.engagement.streak_tier || 'starting',
          });
          setMomentum(response.data.engagement.momentum || 0);
          setOnboarding({
            step: response.data.engagement.onboarding_step || 0,
            featuresDiscovered: response.data.engagement.features_discovered || [],
            completed: response.data.engagement.onboarding_step >= 5,
          });
        }
      } else {
        // Load local streak for anonymous users
        const localStreak = localStorage.getItem(STORAGE_KEYS.LOCAL_STREAK);
        if (localStreak) {
          try {
            setStreak(JSON.parse(localStreak));
          } catch {
            // Ignore parse errors
          }
        }
      }
    } catch (error) {
      console.error('Failed to load engagement:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  // ============================================================================
  // CLAIM ANONYMOUS ENGAGEMENT ON LOGIN
  // ============================================================================
  
  const claimAnonymousEngagement = useCallback(async () => {
    if (!isAuthenticated || hasClaimed || authLoading) return;
    
    try {
      const anonId = anonymousId.current;
      const pendingEvents = getPendingEvents();
      
      console.log(`🔗 Claiming anonymous engagement: ${anonId}`);
      
      const response = await api.post('/api/engagement/claim', {
        anonymousId: anonId,
        pendingEvents,
      });
      
      if (response.data?.success) {
        console.log('✅ Anonymous engagement claimed successfully');
        clearLocalEngagement();
        setHasClaimed(true);
        
        // Update state with claimed data
        if (response.data.engagement) {
          setEngagement(response.data.engagement);
          setStreak({
            current: response.data.engagement.current_streak || 0,
            longest: response.data.engagement.longest_streak || 0,
            tier: response.data.engagement.streak_tier || 'starting',
          });
        }
      }
    } catch (error) {
      console.error('Failed to claim engagement:', error);
    }
  }, [isAuthenticated, hasClaimed, authLoading]);

  // ============================================================================
  // EVENT TRACKING
  // ============================================================================
  
  /**
   * Track an engagement event
   */
  const trackEvent = useCallback((eventType, eventCategory, eventData = {}) => {
    const event = {
      eventType,
      eventCategory,
      eventData,
      pagePath: window.location.pathname,
      sessionId: sessionId.current,
      timestamp: new Date().toISOString(),
    };
    
    // Add to queue
    eventQueue.current.push(event);
    
    // If authenticated, flush immediately for important events
    const importantEvents = ['lead_approved', 'message_sent', 'reply_received'];
    if (isAuthenticated && importantEvents.includes(eventType)) {
      flushEvents();
    }
    
    // Otherwise, batch and flush periodically
    if (eventQueue.current.length >= EVENT_BATCH_SIZE) {
      flushEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  
  /**
   * Flush event queue to backend or localStorage
   */
  const flushEvents = useCallback(async () => {
    if (eventQueue.current.length === 0) return;
    
    const events = [...eventQueue.current];
    eventQueue.current = [];
    
    if (isAuthenticated) {
      // Send to backend
      try {
        for (const event of events) {
          await api.post('/api/engagement/event', {
            ...event,
            anonymousId: anonymousId.current,
          });
        }
      } catch (error) {
        console.warn('Failed to send events:', error);
        // Add back to queue on failure
        eventQueue.current = [...events, ...eventQueue.current];
      }
    } else {
      // Store locally for later claim
      const pending = getPendingEvents();
      savePendingEvents([...pending, ...events]);
    }
  }, [isAuthenticated]);

  // ============================================================================
  // CONVENIENCE TRACKING METHODS
  // ============================================================================
  
  const trackSearch = useCallback((query, mode) => {
    trackEvent('search_executed', 'search', { query, mode });
  }, [trackEvent]);
  
  const trackLeadView = useCallback((leadData) => {
    trackEvent('lead_viewed', 'lead', {
      name: leadData?.firstName ? `${leadData.firstName} ${leadData.lastName || ''}` : leadData?.name,
      company: leadData?.company?.name || leadData?.companyName,
      title: leadData?.title,
    });
  }, [trackEvent]);
  
  const trackLeadApproved = useCallback((leadData) => {
    trackEvent('lead_approved', 'lead', {
      name: leadData?.firstName ? `${leadData.firstName} ${leadData.lastName || ''}` : leadData?.name,
      company: leadData?.company?.name || leadData?.companyName,
      title: leadData?.title,
      industry: leadData?.company?.industry || leadData?.industry,
    });
  }, [trackEvent]);
  
  const trackLeadSkipped = useCallback((leadData) => {
    trackEvent('lead_skipped', 'lead', {
      name: leadData?.firstName ? `${leadData.firstName} ${leadData.lastName || ''}` : leadData?.name,
      company: leadData?.company?.name || leadData?.companyName,
    });
  }, [trackEvent]);
  
  const trackMessageSent = useCallback((recipientName) => {
    trackEvent('message_sent', 'outreach', { recipient: recipientName });
  }, [trackEvent]);
  
  const trackFeatureDiscovered = useCallback((featureName) => {
    trackEvent('feature_discovered', 'onboarding', { feature: featureName });
  }, [trackEvent]);
  
  const trackPageView = useCallback((pagePath) => {
    trackEvent('page_view', 'navigation', { path: pagePath || window.location.pathname });
  }, [trackEvent]);

  // ============================================================================
  // REFRESH STREAK
  // ============================================================================
  
  const refreshStreak = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get('/api/engagement/streak');
      if (response.data?.success) {
        setStreak(response.data.streak);
      }
    } catch (error) {
      console.error('Failed to refresh streak:', error);
    }
  }, [isAuthenticated]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Load engagement on auth change
  useEffect(() => {
    loadEngagement();
  }, [loadEngagement]);
  
  // Claim anonymous engagement on login
  useEffect(() => {
    if (isAuthenticated && !authLoading && !hasClaimed) {
      claimAnonymousEngagement();
    }
  }, [isAuthenticated, authLoading, hasClaimed, claimAnonymousEngagement]);
  
  // Track session start
  useEffect(() => {
    trackEvent('session_start', 'session', {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount
  
  // Set up periodic event flushing
  useEffect(() => {
    flushTimerRef.current = setInterval(flushEvents, EVENT_FLUSH_INTERVAL);
    
    return () => {
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
      }
      // Flush remaining events on unmount
      flushEvents();
    };
  }, [flushEvents]);
  
  // Flush events before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushEvents();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushEvents]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // State
    engagement,
    streak,
    momentum,
    onboarding,
    isLoading,
    
    // IDs
    anonymousId: anonymousId.current,
    sessionId: sessionId.current,
    
    // Actions
    trackEvent,
    trackSearch,
    trackLeadView,
    trackLeadApproved,
    trackLeadSkipped,
    trackMessageSent,
    trackFeatureDiscovered,
    trackPageView,
    
    // Refresh
    refreshStreak,
    refreshEngagement: loadEngagement,
  };

  return (
    <EngagementContext.Provider value={value}>
      {children}
    </EngagementContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Full engagement context hook
 */
export function useEngagement() {
  const context = useContext(EngagementContext);
  if (!context) {
    throw new Error('useEngagement must be used within an EngagementProvider');
  }
  return context;
}

/**
 * Quick access to streak data
 */
export function useStreak() {
  const context = useContext(EngagementContext);
  if (!context) {
    // Return defaults if outside provider (for landing pages)
    return { current: 0, longest: 0, tier: 'starting' };
  }
  return context.streak;
}

/**
 * Onboarding progress hook
 */
export function useOnboarding() {
  const context = useContext(EngagementContext);
  if (!context) {
    return { step: 0, featuresDiscovered: [], completed: false };
  }
  return context.onboarding;
}

export default EngagementContext;






